import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Alert,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import DashboardService from '../../services/DashboardService.js';
import EInvoiceService from '../../services/EInvoiceService.js';

const Reports = () => {
  const [period, setPeriod] = useState('month');
  const [showEInvoice, setShowEInvoice] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [appointments, setAppointments] = useState(null);
  const [patients, setPatients] = useState(null);
  const [lowStock, setLowStock] = useState([]);
  const [einvoiceStatus, setEinvoiceStatus] = useState({});

  const dateRange = useMemo(() => {
    const end = new Date();
    const start = new Date(end);
    switch (period) {
      case 'week':
        start.setDate(end.getDate() - 7);
        break;
      case 'quarter':
        start.setMonth(end.getMonth() - 3);
        break;
      case 'year':
        start.setFullYear(end.getFullYear() - 1);
        break;
      case 'month':
      default:
        start.setMonth(end.getMonth() - 1);
        break;
    }
    return { start, end };
  }, [period]);

  const unwrapPayload = (res) => {
    if (res == null) return null;
    const payload = res?.data ?? res;
    if (typeof payload === 'string') {
      try {
        return JSON.parse(payload);
      } catch {
        return payload;
      }
    }
    return payload;
  };

  const toPairs = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) {
      if (value.every(v => v && typeof v === 'object' && ('Month' in v || 'month' in v) && ('Amount' in v || 'amount' in v || 'Count' in v || 'count' in v))) {
        return value.map(v => [
          v.month ?? v.Month,
          v.amount ?? v.Amount ?? v.count ?? v.Count
        ]);
      }
      return value.map((v, idx) => [String(idx), v]);
    }
    if (value && typeof value === 'object' && Array.isArray(value.$values)) {
      return toPairs(value.$values);
    }
    if (typeof value === 'object') {
      return Object.entries(value);
    }
    return [];
  };

  const toNumber = (value) => {
    if (value == null) return 0;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const n = Number(value);
      return Number.isFinite(n) ? n : 0;
    }
    return 0;
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const promises = [
        DashboardService.getDashboardSummary(),
        DashboardService.getRevenueStatistics(period),
        DashboardService.getAppointmentStatistics ? DashboardService.getAppointmentStatistics(period) : Promise.resolve({ data: null }),
        DashboardService.getPatientStatistics(period),
        DashboardService.getLowStockItems(10),
      ];
      
      // Only fetch E-Invoice status summary if enabled
      if (showEInvoice) {
        promises.push(EInvoiceService.getStatusSummary(dateRange.start.toISOString(), dateRange.end.toISOString()));
      } else {
        promises.push(Promise.resolve({})); // Placeholder for einvoiceStatus
      }

      const results = await Promise.all(promises);
      
      // Destructure results based on whether E-Invoice was included
      const [summaryRes, revenueRes, apptRes, patientRes, lowStockRes, statusSummary] = results;
      
      setSummary(unwrapPayload(summaryRes));
      setRevenue(unwrapPayload(revenueRes));
      setAppointments(unwrapPayload(apptRes));
      setPatients(unwrapPayload(patientRes));
      setLowStock(unwrapPayload(lowStockRes) ?? []);
      setEinvoiceStatus(unwrapPayload(statusSummary) ?? {});
    } catch (err) {
      console.error('Error loading reports:', err);
      setError('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, showEInvoice]);

  const exportCsv = (filename, rows) => {
    if (!rows || rows.length === 0) return;
    const headers = Object.keys(rows[0]);
    const csv = [headers.join(',')]
      .concat(rows.map(r => headers.map(h => JSON.stringify(r[h] ?? '')).join(',')))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const revenueTotals = useMemo(() => {
    const data = revenue ?? {};
    const monthly = data.monthlyRevenue ?? data.MonthlyRevenue ?? null;

    return {
      totalBilled: toNumber(data.totalBilled ?? data.TotalBilled),
      totalRevenue: toNumber(data.totalRevenue ?? data.TotalRevenue),
      outstandingAmount: toNumber(data.outstandingAmount ?? data.OutstandingAmount),
      monthlyRevenue: monthly,
    };
  }, [revenue]);

  const monthlyRevenuePairs = useMemo(() => {
    return toPairs(revenueTotals.monthlyRevenue);
  }, [revenueTotals.monthlyRevenue]);

  const patientStats = useMemo(() => {
    const data = patients ?? {};
    return {
      totalPatients: toNumber(data.totalPatients ?? data.TotalPatients),
      newPatients: toNumber(data.newPatients ?? data.NewPatients),
      monthlyRegistrations: data.monthlyRegistrations ?? data.MonthlyRegistrations ?? null,
      activeVsInactive: data.activeVsInactive ?? data.ActiveVsInactive ?? null,
    };
  }, [patients]);

  const monthlyRegistrationsPairs = useMemo(() => {
    return toPairs(patientStats.monthlyRegistrations);
  }, [patientStats.monthlyRegistrations]);

  const activeVsInactivePairs = useMemo(() => {
    return toPairs(patientStats.activeVsInactive);
  }, [patientStats.activeVsInactive]);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" fontWeight="bold">Reports</Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showEInvoice}
                onChange={(e) => setShowEInvoice(e.target.checked)}
                color="primary"
              />
            }
            label="Show E-Invoice Reports"
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="period-label">Period</InputLabel>
            <Select
              labelId="period-label"
              label="Period"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            >
              <MenuItem value="week">Last 7 days</MenuItem>
              <MenuItem value="month">Last 30 days</MenuItem>
              <MenuItem value="quarter">Last 3 months</MenuItem>
              <MenuItem value="year">Last 12 months</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Revenue" action={
              <Button size="small" onClick={() => {
                const rows = [];
                monthlyRevenuePairs.forEach(([month, amount]) => {
                  rows.push({ Month: month, Amount: amount });
                });
                rows.push({ Metric: 'TotalRevenue', Value: revenueTotals.totalRevenue });
                rows.push({ Metric: 'TotalBilled', Value: revenueTotals.totalBilled });
                rows.push({ Metric: 'Outstanding', Value: revenueTotals.outstandingAmount });
                exportCsv('revenue.csv', rows);
              }}>Export CSV</Button>
            }/>
            <Divider />
            <CardContent>
              <Typography variant="body1">Total Billed: RM {Number(revenueTotals.totalBilled ?? 0).toFixed(2)}</Typography>
              <Typography variant="body1">Total Revenue: RM {Number(revenueTotals.totalRevenue ?? 0).toFixed(2)}</Typography>
              <Typography variant="body1">Outstanding: RM {Number(revenueTotals.outstandingAmount ?? 0).toFixed(2)}</Typography>
              <Box mt={2}>
                <Typography variant="subtitle2">Monthly Revenue</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Month</TableCell>
                      <TableCell>Amount (RM)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {monthlyRevenuePairs.length > 0 ? (
                      monthlyRevenuePairs.map(([month, amount]) => (
                        <TableRow key={month}>
                          <TableCell>{month}</TableCell>
                          <TableCell>{toNumber(amount).toFixed(2)}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow><TableCell colSpan={2}>No data</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {showEInvoice && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="E-Invoice Status Summary" action={
                <Button size="small" onClick={() => {
                  const rows = Object.entries(einvoiceStatus).map(([status, count]) => ({ Status: status, Count: count }));
                  exportCsv('einvoice_status.csv', rows);
                }}>Export CSV</Button>
              }/>
              <Divider />
              <CardContent>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Count</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {einvoiceStatus && Object.keys(einvoiceStatus).length > 0 ? (
                      Object.entries(einvoiceStatus).map(([status, count]) => (
                        <TableRow key={status}>
                          <TableCell>{status}</TableCell>
                          <TableCell align="right">{count}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow><TableCell colSpan={2}>No data</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Patient Statistics" />
            <Divider />
            <CardContent>
              {patients ? (
                <Box>
                  <Typography variant="body1" gutterBottom>Total Patients: {patientStats.totalPatients}</Typography>
                  <Typography variant="body1" gutterBottom>New Patients: {patientStats.newPatients}</Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2">Monthly Registrations</Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Month</TableCell>
                          <TableCell align="right">Count</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {monthlyRegistrationsPairs.length > 0 ? (
                          monthlyRegistrationsPairs.map(([month, count]) => (
                            <TableRow key={month}>
                              <TableCell>{month}</TableCell>
                              <TableCell align="right">{toNumber(count)}</TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow><TableCell colSpan={2}>No data</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2">Active vs Inactive</Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Count</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {activeVsInactivePairs.length > 0 ? (
                          activeVsInactivePairs.map(([status, count]) => (
                            <TableRow key={status}>
                              <TableCell>{status}</TableCell>
                              <TableCell align="right">{toNumber(count)}</TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow><TableCell colSpan={2}>No data</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2">No data</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Appointment Statistics" />
            <Divider />
            <CardContent>
              {appointments ? (
                <Box>
                  <Typography variant="body1" gutterBottom>
                    Total Appointments: {appointments.totalAppointments || appointments.TotalAppointments || 0}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    Total Persons: {appointments.totalPersons || appointments.TotalPersons || 0}
                  </Typography>
                  
                  {appointments.statusDistribution || appointments.StatusDistribution ? (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>Status Distribution:</Typography>
                      {Object.entries(appointments.statusDistribution || appointments.StatusDistribution).map(([status, count]) => (
                        <Box key={status} display="flex" justifyContent="space-between" sx={{ ml: 2, mr: 2, borderBottom: '1px dashed #eee' }}>
                          <Typography variant="body2">{status}</Typography>
                          <Typography variant="body2">{count}</Typography>
                        </Box>
                      ))}
                    </Box>
                  ) : null}

                  {appointments.dailyAppointments || appointments.DailyAppointments ? (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>Daily Breakdown:</Typography>
                      <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                        {Object.entries(appointments.dailyAppointments || appointments.DailyAppointments).map(([date, count]) => (
                          <Box key={date} display="flex" justifyContent="space-between" sx={{ ml: 2, mr: 2 }}>
                            <Typography variant="body2">{new Date(date).toLocaleDateString()}</Typography>
                            <Typography variant="body2">{count}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ) : null}
                </Box>
              ) : (
                <Typography variant="body2">No data</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardHeader title="Low Stock Items" action={
              <Button size="small" onClick={() => {
                const rows = lowStock.map(i => ({
                  Name: i.itemName || i.ItemName || i.name || i.Name,
                  SKU: i.sku || i.SKU,
                  Quantity: i.currentStock ?? i.CurrentStock ?? i.quantity ?? i.Quantity,
                  ReorderLevel: i.minimumStock ?? i.MinimumStock ?? i.reorderLevel ?? i.ReorderLevel,
                }));
                exportCsv('low_stock.csv', rows);
              }}>Export CSV</Button>
            }/>
            <Divider />
            <CardContent>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell align="right">Current Stock</TableCell>
                    <TableCell align="right">Min. Stock</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lowStock && lowStock.length > 0 ? (
                    lowStock.map(item => (
                      <TableRow key={item.inventoryItemId || item.InventoryItemId || item.itemId || item.ItemId}>
                        <TableCell>{item.itemName || item.ItemName || item.name || item.Name}</TableCell>
                        <TableCell>{item.sku || item.SKU}</TableCell>
                        <TableCell align="right">{item.currentStock ?? item.CurrentStock ?? item.quantity ?? item.Quantity}</TableCell>
                        <TableCell align="right">{item.minimumStock ?? item.MinimumStock ?? item.reorderLevel ?? item.ReorderLevel}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow><TableCell colSpan={4}>No data</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Reports;
